<script setup lang="ts">
import { computed } from 'vue'
import Select from '~/design-system/atoms/Select/Select.atom.vue'

interface Props {
  /** Accessible name of the select control. */
  ariaLabel: string
  /** Options displayed in the select menu. */
  options: { label: string; value: string | number }[]
  /** Whether the default top spacing is omitted. */
  removeTopSpacing?: boolean
  /** Placeholder shown when no value is selected. */
  placeholder?: string
  /** Additional classes applied to the select button. */
  buttonClass?: string
}

const props = withDefaults(defineProps<Props>(), {
  buttonClass: '',
  placeholder: 'Select an option',
  removeTopSpacing: false
})

const modelValue = defineModel<string | number>()

const hasSelection = (value: string | number | undefined): value is string | number =>
  value !== undefined && value !== '' && value !== 0 && !Number.isNaN(value)

const optionKeys = computed(() =>
  props.options.map((option, index) => ({ label: option.label, value: String(index) }))
)

const activeKey = computed(() => {
  const activeValue = modelValue.value
  if (!hasSelection(activeValue)) return ''
  const index = props.options.findIndex((option) => option.value == activeValue)
  return index === -1 ? '' : String(index)
})

const handleKeyUpdate = (key: number | string) => {
  const option = props.options[Number(key)]
  if (option !== undefined) modelValue.value = option.value
}
</script>

<template>
  <div :class="props.removeTopSpacing ? '' : 'mt-2'">
    <Select
      :model-value="activeKey"
      :options="optionKeys"
      :ariaLabel="props.ariaLabel"
      :placeholder="props.placeholder"
      :class="props.buttonClass"
      @update:model-value="handleKeyUpdate"
    />
  </div>
</template>
